export const NODE_ENV = import.meta.env.VITE_NODE_ENV
export const SENTRY_DSN =
  import.meta.env.VITE_SENTRY_DSN ||
  'https://9a0cb1fbc220e3e2e7b0bc286d28abb2@o412076.ingest.sentry.io/4506353875681280'
export const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://prod-penning-api-app-service.azurewebsites.net'

if (NODE_ENV === 'development') {
  // biome-ignore lint/suspicious/noConsole: <useful info>
  console.warn('*************** DEVELOPMENT ***************')
}
if (!SENTRY_DSN) {
  // biome-ignore lint/suspicious/noConsole: <useful info>
  console.warn('Warning: Sentry DSN not set!')
}
if (!API_URL) {
  // biome-ignore lint/suspicious/noConsole: <useful info>
  console.error('Error: No api url defined - the website will not work.')
} else if (API_URL.charAt(API_URL.length - 1) === '/') {
  // biome-ignore lint/suspicious/noConsole: <useful info>
  console.error(
    "Error: api url is incorrectly defined, it must not end with a '/'"
  )
}

export const DOMAIN =
  import.meta.env.VITE_DOMAIN || 'https://www.app.penning.dk'

export const BLOB_STORAGE_URL =
  import.meta.env.VITE_BLOB_STORAGE_URL ||
  import.meta.env.BLOB_STORAGE_URL ||
  'https://penningtempstorage.blob.core.windows.net'

/** Public PDFs / assets under the `assets` container, e.g. agreement PDFs */
export const BLOB_ASSETS_BASE_URL =
  import.meta.env.VITE_BLOB_ASSETS_BASE_URL || `${BLOB_STORAGE_URL}/assets`

// Auth0
export const AUTH0_DOMAIN =
  import.meta.env.VITE_AUTH0_DOMAIN || 'penning.eu.auth0.com'
export const AUTH0_CLIENT_ID =
  import.meta.env.VITE_AUTH0_CLIENT_ID || 'pdyOfd6UKlyVdcZfCY1BHZYAED8OVAvu'
export const AUTH0_CALLBACK_URL = `${DOMAIN}/app`
export const AUTH0_LOGOUT_URL = DOMAIN

// Misc.
export const ROUNDING_DIGITS_CRYPTO = 8
export const ROUNDING_DIGITS_FIAT = 2

export const DEFAULT_PAY_CURRENCY_ISO_CODE = 'EUR'
export const DEFAULT_RECEIVE_CURRENCY_ISO_CODE = 'USDC'

export const VERSION = '1.1.3'

export const ARYZE_FEE_STRUCTURE_ID = 'EE3FDD0C-C856-4F5C-A5F7-78AD0723B9B0'

export const APPKIT_PROJECT_ID =
  import.meta.env.APPKIT_PROJECT_ID || '0d7b9f1fca93f261b2fc7ea803391ac9'

/**
 * Temporarily disables the customer-facing entry point into the Invest
 * (portfolio management) product — the "Portfolio Deposits" menu item and the
 * `/app/fund-deposits` route. Customers can still see the menu item (greyed
 * out) and express interest via the onboarding questionnaire, but cannot fund
 * a portfolio deposit.
 *
 * Rationale: we committed to the FCA/Finanstilsynet not to take more customers
 * into the portfolio management product before the suitability assessment
 * ("investment risk appetite") questions are implemented. Flip this back to
 * `false` once that workstream ships. See ADR-0005 ("Out of scope").
 */
export const INVEST_DEPOSITS_DISABLED = true

/**
 * The Veli → Penning move happens in two steps, each with its own date:
 *
 *  1. Portfolio move ({@link VELI_PORTFOLIO_MOVE_DATE_LABEL}): the client's
 *     portfolio moves from Veli to Penning, which safeguards and manages it
 *     from then on — same holdings, same strategy. Nothing changes for the
 *     client at this point except who holds the portfolio behind the scenes.
 *  2. App cutover ({@link VELI_CUTOVER_DATE_LABEL}): the Veli app becomes the
 *     Penning app and the real platform unlocks. This is the only step the
 *     client actually sees in their day-to-day.
 *
 * These dates anchor the whole Veli interim experience — surfaced on the
 * interim screens so the wait reads as "I know what happens and when" rather
 * than anxious limbo. They are planning targets, not hard contractual
 * deadlines (see the Veli migration thread / ADR-0004). Two display forms keep
 * the copy readable without re-formatting a Date at each call site.
 */
export const VELI_PORTFOLIO_MOVE_DATE_LABEL = '30 June 2026'
export const VELI_PORTFOLIO_MOVE_DATE_SHORT = '30 Jun 2026'
export const VELI_CUTOVER_DATE_LABEL = '25 August 2026'
export const VELI_CUTOVER_DATE_SHORT = '25 Aug 2026'

/**
 * A time-boxed, app-wide notice that support is running on reduced staffing
 * (summer holidays) so customers calibrate their expectations for response
 * times. Rendered as a banner in the app chrome only while today falls within
 * the window; outside it the banner never shows, so this can be left in place
 * and simply lapses on its own. To retire it early, clear the window (or set
 * `endsOn` to a past date); to run it again next year, move the window.
 *
 * `startsOn` / `endsOn` are inclusive calendar dates (`YYYY-MM-DD`) evaluated
 * against the viewer's local day — see {@link isSupportHolidayNoticeActive}.
 * Keep the human dates in `message` in sync with the window.
 */
export const SUPPORT_HOLIDAY_NOTICE = {
  startsOn: '2026-07-18',
  endsOn: '2026-08-03',
  message:
    'We have limited staffing due to summer holidays from July 18th to August 3rd. Please expect longer response times for support inquiries.',
} as const
